import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import type { OnboardingScreenDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { IconButton } from "@/components/Button/IconButton";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { DocumentInput } from "@/components/DocumentInput/DocumentInput";
import type { FormDocument } from "@/components/DocumentInput/useDocumentInput";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { LinkExternal01Icon } from "@/components/Icons/LinkExternal01Icon";
import { XCloseIcon } from "@/components/Icons/XCloseIcon";
import { OnboardingScreen } from "@/components/OnboardingScreen/OnboardingScreen";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { RadioCard } from "@/components/Radio/RadioCard";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { SectionCard } from "@/design-system/SectionCard";
import { Text } from "@/design-system/Text";
import { ALLOWED_FILE_TYPES } from "@/helpers/file-types";
import { canManageProjectManagement } from "@/helpers/permissions";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadDocument } from "@/hooks/Network/useUploadDocument";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { onboardingScreenMutations } from "@/queries/onboarding-screens/mutations";
import { useOnboardingScreenQueries } from "@/queries/onboarding-screens/queryOptions";
import { projectMutations } from "@/queries/project/mutations";
import { useProjectQueries } from "@/queries/project/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function OnboardingScreenOverviewPage(): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<OnboardingScreenDto>(
    "onboarding-screen-delete-modal",
  );
  const hasPermission = usePermission();
  const sessionUser = useSessionUser();
  const { uploadFormDocument } = useUploadDocument();

  const projectQueries = useProjectQueries();
  const onboardingQueries = useOnboardingScreenQueries();
  const {
    data: onboardingScreens,
    isPending: isLoadingOnboardingScreens,
    error: onboardingScreensError,
  } = useQuery(onboardingQueries.list());
  const {
    data: projectDetails,
    isPending: isLoadingProjectDetails,
    error: projectDetailsError,
  } = useQuery(projectQueries.details());

  const deleteOnboardingScreenMutation = onboardingScreenMutations.useDeleteOnboardingScreen();
  const editProjectMutation = projectMutations.useEditProject();

  const [codeOfConductType, setCodeOfConductType] = useState<"default" | "custom">();
  const [customDocuments, setCustomDocuments] = useState<FormDocument[]>([]);
  const [hideYoutubeLinks, setHideYoutubeLinks] = useState<boolean>();

  const resolvedCodeOfConductType =
    codeOfConductType ?? (projectDetails?.codeOfConduct.isCustom ? "custom" : "default");
  const resolvedHideYoutubeLinks = hideYoutubeLinks ?? projectDetails?.hideYoutubeLinksInInvitationEmails ?? false;

  const handleSaveSettings = async () => {
    if (!projectDetails) return;

    let customCodeOfConductId: string | undefined;
    if (resolvedCodeOfConductType === "custom") {
      const customDocument =
        customDocuments[0] ?? (projectDetails.codeOfConduct.isCustom ? projectDetails.codeOfConduct : undefined);
      if (customDocument) {
        const uploadedDocument = await uploadFormDocument(customDocument);
        if (uploadedDocument) {
          customCodeOfConductId = uploadedDocument.id;
        }
      }
    }

    await editProjectMutation.mutateAsync({
      name: projectDetails.name,
      city: projectDetails.city,
      owner: projectDetails.owner,
      maintenance: projectDetails.maintenance,
      projectType: projectDetails.type,
      appBranding: projectDetails.appBranding,
      developmentType: projectDetails.developmentType,
      activeState: projectDetails.activeState,
      allowHidePostForAdmin: projectDetails.allowHidePostForAdmin,
      hideYoutubeLinksInInvitationEmails: resolvedHideYoutubeLinks,
      customCodeOfConductId,
    });
  };

  const error = onboardingScreensError || projectDetailsError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const isLoading = isLoadingOnboardingScreens || isLoadingProjectDetails;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const styling = {
    bgColor: projectDetails?.styling.navigationColor,
    textColor: projectDetails?.styling.textLightColor,
  };

  const showGradient = onboardingScreens?.[0] && !!(onboardingScreens[0].title || onboardingScreens[0].subtitle);

  let codeOfConductDocument = customDocuments;
  if (customDocuments.length === 0) {
    codeOfConductDocument = projectDetails?.codeOfConduct.isCustom ? [projectDetails.codeOfConduct] : [];
  }

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={t("page.onboarding-screens.title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: sessionUser.project.name,
              },
              {
                name: t("page.onboarding-screens.title"),
              },
            ]}
          />
        }
        actions={
          hasPermission(canManageProjectManagement) && (
            <Button
              type="link"
              href={routes.onboardingScreens.new({ slug })}
              data-testid="onboarding-screens-create-link"
            >
              {t("page.onboarding-screens.actions.create")}
            </Button>
          )
        }
      >
        <SectionCard>
          <Form.Body>
            <FormField label={t("page.onboarding-screens.form.screens")}>
              {onboardingScreens.length === 0 ? (
                <Text as="p">{t("page.onboarding-screens.list.empty")}</Text>
              ) : (
                <ul className="flex flex-wrap justify-center gap-x-space-md gap-y-space-lg @md:justify-start">
                  {onboardingScreens.map((x) => (
                    <li
                      data-testid="onboarding-screen-previewer-container"
                      className="relative flex w-search-compact flex-col gap-space-md"
                      key={x.id}
                    >
                      <OnboardingScreen
                        imageUrl={x.image.url}
                        title={x.title}
                        subtitle={x.subtitle}
                        styling={styling}
                        withFadeOutBackground={showGradient}
                      />
                      {hasPermission(canManageProjectManagement) && (
                        <Inline gap="sm" justify="center" wrap={false}>
                          <Button
                            fullWidth
                            type="link"
                            variant="ghostPrimary"
                            data-testid="edit-btn"
                            icon={<Edit05Icon />}
                            href={routes.onboardingScreens.edit({ slug, id: x.id })}
                          >
                            {t("page.onboarding-screens.preview.actions.edit")}
                          </Button>
                          <div className="absolute top-0 right-0 z-10 translate-x-1/2 -translate-y-1/2">
                            <IconButton
                              size="xs"
                              isCircular
                              variant="filled"
                              tone="error"
                              icon={<XCloseIcon />}
                              data-testid="delete-btn"
                              onClick={() => openDeleteModal(x)}
                              title={t("common.action.delete")}
                            />
                          </div>
                        </Inline>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </FormField>
            {sessionUser.isSuperAdmin && (
              <>
                <FormField label={t("page.projects.form.code-of-conduct")}>
                  <FormRadioCardGroup>
                    <RadioCard
                      label={t("page.projects.form.code-of-conduct.use-default")}
                      description={t("page.projects.form.code-of-conduct.use-default.description")}
                      checked={resolvedCodeOfConductType === "default"}
                      value="default"
                      name="codeOfConductType"
                      onChange={() => {
                        setCodeOfConductType("default");
                        setCustomDocuments([]);
                      }}
                    >
                      <Button
                        type="link"
                        href={projectDetails?.codeOfConduct.defaultCodeOfConductUrl || ""}
                        variant="ghostSecondary"
                        size="sm"
                        icon={<LinkExternal01Icon />}
                        target="_blank"
                        isExternal
                      >
                        {t("page.projects.form.code-of-conduct.view")}
                      </Button>
                    </RadioCard>
                    <RadioCard
                      label={t("page.projects.form.code-of-conduct.use-custom")}
                      description={t("page.projects.form.code-of-conduct.use-custom.description")}
                      checked={resolvedCodeOfConductType === "custom"}
                      value="custom"
                      name="codeOfConductType"
                      onChange={() => setCodeOfConductType("custom")}
                    >
                      <DocumentInput
                        value={codeOfConductDocument}
                        accept={ALLOWED_FILE_TYPES.DOCUMENT}
                        onChange={(files) => setCustomDocuments(files)}
                      />
                    </RadioCard>
                  </FormRadioCardGroup>
                </FormField>
                {projectDetails?.appBranding !== "areaOfPeople" && (
                  <FormField label={t("page.projects.form.hide-youtube-links-in-invitation-emails")}>
                    <FormRadioCardGroup>
                      <RadioCard
                        name="hideYoutubeLinksInInvitationEmails"
                        description={t("page.projects.form.hide-youtube-links-in-invitation-emails.yes.description")}
                        value="true"
                        label={t("page.projects.form.hide-youtube-links-in-invitation-emails.yes")}
                        checked={resolvedHideYoutubeLinks}
                        onChange={() => setHideYoutubeLinks(true)}
                      />
                      <RadioCard
                        name="hideYoutubeLinksInInvitationEmails"
                        description={t("page.projects.form.hide-youtube-links-in-invitation-emails.no.description")}
                        value="false"
                        label={t("page.projects.form.hide-youtube-links-in-invitation-emails.no")}
                        checked={!resolvedHideYoutubeLinks}
                        onChange={() => setHideYoutubeLinks(false)}
                      />
                    </FormRadioCardGroup>
                  </FormField>
                )}
              </>
            )}
          </Form.Body>
          <Form.Footer
            renderPrimaryAction={() => (
              <Button onClick={handleSaveSettings} isLoading={editProjectMutation.isPending}>
                {t("common.action.save")}
              </Button>
            )}
          />
        </SectionCard>
      </DocumentPage>
      <DeleteModal
        title={t("page.onboarding-screens.actions.delete.title")}
        description={t("page.onboarding-screens.actions.delete.description")}
        onDelete={(onboardingScreen) =>
          deleteOnboardingScreenMutation.mutateAsync({ onboardingScreenId: onboardingScreen.id })
        }
        deleteBtnProps={{
          "data-testid": "onboarding-screens-action-delete",
        }}
        {...deleteModalProps}
      />
    </>
  );
}
